// Sweet Swap - DOM rendering, swipe/tap interaction, and the GameDef export.
//
// The engine speaks in cells; the view maps cell (r,c) -> pixels via the tile
// size d = field width / W. Candy elements live in a parallel els[r][c] grid
// that is rebuilt from the engine's gravity report after every cascade step.

import { el, clear } from "../../lib/dom";
import { sfx, haptic } from "../../lib/feedback";
import type { GameDef, LevelView, LevelViewOptions } from "../types";
import {
  H,
  W,
  type Board,
  type Cell,
  adjacent,
  applyGravity,
  clearStep,
  fillBoard,
  findMove,
  generateSwapLevel,
  makeRng,
  shuffleBoard,
  swapCells,
  swapMakesMatch,
} from "./engine";
import "./sweetswap.css";

function starsFor(movesLeft: number, total: number): number {
  if (movesLeft >= Math.ceil(total * 0.3)) return 3;
  if (movesLeft >= Math.ceil(total * 0.1)) return 2;
  return 1;
}

function createLevelView(opts: LevelViewOptions): LevelView {
  const level = generateSwapLevel(opts.level);

  let rng = makeRng(level.seed);
  let board: Board = fillBoard(rng, level.colors);
  let els: (HTMLElement | null)[][] = [];
  let score = 0;
  let movesLeft = level.moves;
  let finished = false;
  let busy = false; // an animation/cascade is running
  let session = 0; // bumped on restart/destroy so stale async loops bail out
  let selected: [number, number] | null = null;
  const timers: Array<ReturnType<typeof setTimeout>> = [];

  const root = opts.mount;
  clear(root);
  root.classList.add("sw");

  // ---- DOM skeleton ----
  // HUD: score card with progress bar + star milestones, and a moves card.
  const STAR_PCTS = [33, 66, 99];
  const scoreVal = el("span.sw-scoreval");
  const barFill = el("div.sw-barfill");
  const starEls = STAR_PCTS.map((pct) => {
    const star = el("span.sw-star", {}, "⭐");
    star.style.left = `${pct}%`;
    return star;
  });
  const scoreCard = el(
    "div.sw-card.sw-scorecard",
    {},
    el("div.sw-cardrow", {}, el("span.sw-label", {}, "Score"), scoreVal),
    el("div.sw-bar", {}, barFill, ...starEls),
  );
  const movesNum = el("div.sw-movesnum");
  const movesCard = el("div.sw-card.sw-movescard", {}, movesNum, el("span.sw-label", {}, "Zetten"));
  const hud = el("div.sw-hud", {}, scoreCard, movesCard);
  const goal = el(
    "div.sw-goalwrap",
    {},
    el("span.sw-goal", {}, `🎯 Haal ${level.target} punten in ${level.moves} zetten`),
  );

  const bgLayer = el("div.sw-bglayer");
  const layer = el("div.sw-layer");
  const fieldEl = el("div.sw-field", {}, bgLayer, layer);
  const panel = el("div.sw-panel", {}, fieldEl);
  const stage = el("div.sw-stage", {}, panel);
  const comboEl = el("span.sw-combo", {}, "Combo ×1");
  const toolbar = el(
    "div.sw-toolbar",
    {},
    el("button.sw-btn", { onclick: showHint, "aria-label": "Hint" }, "💡 Hint"),
    comboEl,
    el("button.sw-btn", { onclick: doRestart, "aria-label": "Opnieuw" }, "↺ Opnieuw"),
  );
  root.append(hud, goal, stage, toolbar);

  let overlay: HTMLElement | null = null;

  // Checkerboard cell backgrounds (static; sized in layout()).
  const cellBgs: HTMLElement[] = [];
  for (let r = 0; r < H; r++) {
    for (let c = 0; c < W; c++) {
      const bg = el((r + c) % 2 === 0 ? "div.sw-cellbg" : "div.sw-cellbg.sw-cellbg-alt");
      cellBgs.push(bg);
      bgLayer.append(bg);
    }
  }

  function later(fn: () => void, ms: number): void {
    timers.push(setTimeout(fn, ms));
  }
  function sleep(ms: number): Promise<void> {
    return new Promise((res) => later(res, ms));
  }

  // ---- Metrics (recomputed on resize) ----
  let d = 0; // tile size in px
  function layout(): void {
    const w = fieldEl.clientWidth;
    if (w === 0) {
      later(layout, 60);
      return;
    }
    d = w / W;
    cellBgs.forEach((bg, i) => {
      const r = Math.floor(i / W);
      const c = i % W;
      bg.style.width = `${d}px`;
      bg.style.height = `${d}px`;
      bg.style.transform = `translate(${c * d}px, ${r * d}px)`;
    });
    for (let r = 0; r < H; r++) {
      for (let c = 0; c < W; c++) {
        const node = els[r]?.[c];
        if (node) sizeAndPlace(node, r, c);
      }
    }
  }
  const onResize = (): void => layout();
  window.addEventListener("resize", onResize);

  function placeAt(node: HTMLElement, r: number, c: number): void {
    const off = d * 0.06;
    // Position via the individual `translate` property (not `transform`) so it
    // never shares a property with the scale-based pop/bounce animations.
    // Mixing position and scale on `transform` makes Safari momentarily reset
    // the candy to the corner and snap back (the "disappear + fly back" glitch).
    node.style.translate = `${c * d + off}px ${r * d + off}px`;
  }

  function sizeAndPlace(node: HTMLElement, r: number, c: number): void {
    const s = d * 0.88;
    node.style.width = `${s}px`;
    node.style.height = `${s}px`;
    node.style.fontSize = `${d * 0.5}px`;
    placeAt(node, r, c);
  }

  // ---- Rendering ----
  function candyNode(cell: Cell): HTMLElement {
    const node = el(`div.sw-candy.sw-c-${(cell.color % 6) + 1}`);
    if (cell.special === 1) node.classList.add("sw-s-1");
    if (cell.special === 2) node.classList.add("sw-s-2");
    if (cell.special === 3) node.classList.add("sw-s-3");
    return node;
  }

  function buildBoard(): void {
    clear(layer);
    els = [];
    for (let r = 0; r < H; r++) {
      const row: (HTMLElement | null)[] = [];
      for (let c = 0; c < W; c++) {
        const cell = board[r][c];
        if (cell) {
          const node = candyNode(cell);
          node.classList.add("sw-in");
          sizeAndPlace(node, r, c);
          layer.append(node);
          row.push(node);
        } else {
          row.push(null);
        }
      }
      els.push(row);
    }
  }

  function updateStatus(): void {
    movesNum.textContent = String(movesLeft);
    movesCard.classList.toggle("sw-low", movesLeft <= 5);
    clear(scoreVal);
    scoreVal.append(String(score), el("span.sw-scoretarget", {}, ` / ${level.target}`));
    const pct = Math.min(100, Math.round((score / level.target) * 100));
    barFill.style.width = `${pct}%`;
    starEls.forEach((star, i) => {
      const on = pct >= STAR_PCTS[i];
      if (on && !star.classList.contains("on")) {
        star.classList.add("sw-starpop");
        later(() => star.classList.remove("sw-starpop"), 700);
      }
      star.classList.toggle("on", on);
    });
    opts.setStatus?.(`🎯 ${score}/${level.target}`);
  }

  function setCombo(chain: number): void {
    comboEl.textContent = `Combo ×${chain}`;
    comboEl.classList.toggle("hot", chain >= 2);
    if (chain >= 2) {
      comboEl.classList.remove("sw-combopop");
      void comboEl.offsetWidth;
      comboEl.classList.add("sw-combopop");
    }
  }

  function floatNote(text: string, x: number, y: number, big = false): void {
    const node = el(big ? "div.sw-points.sw-note" : "div.sw-points", {}, text);
    node.style.left = `${x}px`;
    node.style.top = `${y}px`;
    fieldEl.append(node);
    later(() => node.remove(), 900);
  }

  // ---- Clear effects: beams, blasts, sparks, shakes ----
  function shake(): void {
    panel.classList.remove("sw-shake");
    void panel.offsetWidth;
    panel.classList.add("sw-shake");
  }

  /** White light sweep across the row/column a striped candy just wiped. */
  function beamFx(r: number, c: number, special: number): void {
    if (special === 3) {
      blastFx(r, c);
      return;
    }
    const node = el(special === 1 ? "div.sw-beam.sw-beam-h" : "div.sw-beam.sw-beam-v");
    if (special === 1) {
      node.style.top = `${r * d}px`;
      node.style.height = `${d}px`;
    } else {
      node.style.left = `${c * d}px`;
      node.style.width = `${d}px`;
    }
    fieldEl.append(node);
    later(() => node.remove(), 550);
  }

  /** Expanding shockwave ring where a color bomb went off. */
  function blastFx(r: number, c: number): void {
    const node = el("div.sw-blast");
    const size = d * 1.3;
    node.style.width = `${size}px`;
    node.style.height = `${size}px`;
    node.style.left = `${(c + 0.5) * d}px`;
    node.style.top = `${(r + 0.5) * d}px`;
    fieldEl.append(node);
    later(() => node.remove(), 700);
    shake();
  }

  /** Little sparks flying out of cleared candies (sampled, capped for perf). */
  function sparkFx(cells: Array<[number, number]>): void {
    const step = Math.max(1, Math.ceil(cells.length / 20));
    for (let i = 0; i < cells.length; i += step) {
      const [r, c] = cells[i];
      for (let k = 0; k < 3; k++) {
        const node = el(k % 2 === 0 ? "div.sw-spark" : "div.sw-spark.sw-spark-w");
        const ang = Math.random() * Math.PI * 2;
        const dist = d * (0.5 + Math.random() * 0.8);
        node.style.left = `${(c + 0.5) * d}px`;
        node.style.top = `${(r + 0.5) * d}px`;
        node.style.setProperty("--dx", `${Math.cos(ang) * dist}px`);
        node.style.setProperty("--dy", `${Math.sin(ang) * dist}px`);
        fieldEl.append(node);
        later(() => node.remove(), 700);
      }
    }
  }

  /** Squash-and-stretch bounce on a batch of candies that just landed. One
   * shared reflow re-triggers the animation cleanly without forcing a layout
   * per candy (which janks on a big cascade). */
  function landFx(nodes: Array<HTMLElement | null>): void {
    const landed = nodes.filter((n): n is HTMLElement => n !== null);
    if (landed.length === 0) return;
    for (const node of landed) node.classList.remove("sw-land");
    void layer.offsetWidth;
    for (const node of landed) {
      node.classList.add("sw-land");
      later(() => node.classList.remove("sw-land"), 260);
    }
  }

  // ---- Selection ----
  function clearSelection(): void {
    if (selected) els[selected[0]][selected[1]]?.classList.remove("sw-sel");
    selected = null;
  }

  function select(r: number, c: number): void {
    clearSelection();
    selected = [r, c];
    els[r][c]?.classList.add("sw-sel");
    sfx.tap();
    haptic(5);
  }

  // ---- Input: swipe to swap, or tap-tap two adjacent candies ----
  let pressCell: [number, number] | null = null;
  let pressX = 0;
  let pressY = 0;
  let dragged = false;

  function cellFromEvent(e: PointerEvent): [number, number] | null {
    if (d === 0) return null;
    const rect = fieldEl.getBoundingClientRect();
    const c = Math.floor((e.clientX - rect.left) / d);
    const r = Math.floor((e.clientY - rect.top) / d);
    return r >= 0 && r < H && c >= 0 && c < W ? [r, c] : null;
  }

  function onPointerDown(e: PointerEvent): void {
    if (busy || finished || overlay) return;
    const cell = cellFromEvent(e);
    if (!cell) return;
    pressCell = cell;
    pressX = e.clientX;
    pressY = e.clientY;
    dragged = false;
    try {
      stage.setPointerCapture(e.pointerId);
    } catch {
      /* synthetic events / stale pointer ids have no capturable pointer */
    }
  }

  function onPointerMove(e: PointerEvent): void {
    if (!pressCell || dragged || busy || finished) return;
    const dx = e.clientX - pressX;
    const dy = e.clientY - pressY;
    if (Math.hypot(dx, dy) < 0.35 * d) return;
    dragged = true;
    const [r, c] = pressCell;
    const target: [number, number] =
      Math.abs(dx) > Math.abs(dy) ? [r, c + Math.sign(dx)] : [r + Math.sign(dy), c];
    if (target[0] < 0 || target[0] >= H || target[1] < 0 || target[1] >= W) return;
    clearSelection();
    void trySwap(pressCell, target);
  }

  function onPointerUp(e: PointerEvent): void {
    const cell = pressCell;
    pressCell = null;
    if (!cell || dragged || busy || finished || overlay) return;
    const up = cellFromEvent(e) ?? cell;
    if (selected && adjacent(selected, up)) {
      void trySwap(selected, up);
      return;
    }
    if (selected && selected[0] === up[0] && selected[1] === up[1]) {
      clearSelection();
      return;
    }
    select(up[0], up[1]);
  }

  stage.addEventListener("pointerdown", onPointerDown);
  stage.addEventListener("pointermove", onPointerMove);
  stage.addEventListener("pointerup", onPointerUp);
  stage.addEventListener("pointercancel", () => {
    pressCell = null;
  });

  // ---- Swapping & cascades ----
  async function trySwap(a: [number, number], b: [number, number]): Promise<void> {
    if (busy || finished || overlay) return;
    if (!adjacent(a, b)) return;
    const ea = els[a[0]][a[1]];
    const eb = els[b[0]][b[1]];
    if (!ea || !eb) return;
    const s = ++session;
    busy = true;
    clearSelection();

    // Swapping a color bomb is always legal; it detonates instead of matching.
    const ca = board[a[0]][a[1]];
    const cb = board[b[0]][b[1]];
    const bombSwap = ca?.special === 3 || cb?.special === 3;
    const ok = bombSwap || swapMakesMatch(board, a, b);
    placeAt(ea, b[0], b[1]);
    placeAt(eb, a[0], a[1]);
    sfx.slide();
    haptic(5);
    await sleep(200);
    if (s !== session) return;

    if (!ok) {
      // Illegal swap: shake them back.
      placeAt(ea, a[0], a[1]);
      placeAt(eb, b[0], b[1]);
      sfx.error();
      haptic([8, 20, 8]);
      await sleep(200);
      if (s === session) busy = false;
      return;
    }

    swapCells(board, a, b);
    els[a[0]][a[1]] = eb;
    els[b[0]][b[1]] = ea;
    movesLeft--;
    updateStatus();

    let forced: Array<[number, number]> | undefined;
    if (bombSwap) {
      forced = [];
      if (ca?.special === 3 && cb?.special === 3) {
        // Two bombs together: wipe the entire board.
        for (let r = 0; r < H; r++) for (let c = 0; c < W; c++) if (board[r][c]) forced.push([r, c]);
      } else {
        // After the swap the bomb sits on its partner's old cell; it clears
        // every candy of the partner's color. Downgrade the bomb itself so it
        // doesn't also fire its own run color through the cascade queue.
        const bombPos = ca?.special === 3 ? b : a;
        const color = (ca?.special === 3 ? cb! : ca!).color;
        board[bombPos[0]][bombPos[1]]!.special = 0;
        blastFx(bombPos[0], bombPos[1]);
        forced.push(bombPos);
        for (let r = 0; r < H; r++) {
          for (let c = 0; c < W; c++) {
            if (board[r][c]?.color === color) forced.push([r, c]);
          }
        }
      }
      floatNote("Kleurenbom! 💥", (W * d) / 2, (H * d) / 2, true);
    }

    await resolveCascades(s, b, forced);
    if (s !== session) return;
    afterSettle();
  }

  async function resolveCascades(
    s: number,
    focus: [number, number] | null,
    forced?: Array<[number, number]>,
  ): Promise<void> {
    let chain = 1;
    for (;;) {
      const step = clearStep(board, chain, chain === 1 ? focus : null, chain === 1 ? forced : undefined);
      if (!step) return;
      score += step.points;
      setCombo(chain);

      let sx = 0;
      let sy = 0;
      for (const [r, c] of step.cleared) {
        sx += (c + 0.5) * d;
        sy += (r + 0.5) * d;
        const node = els[r][c];
        els[r][c] = null;
        if (node) {
          node.classList.add("sw-pop");
          later(() => node.remove(), 340);
        }
      }
      for (const sp of step.spawned) {
        els[sp.r][sp.c]?.remove();
        const node = candyNode(sp.cell);
        node.classList.add("sw-in");
        sizeAndPlace(node, sp.r, sp.c);
        layer.append(node);
        els[sp.r][sp.c] = node;
        if (sp.cell.special === 3) {
          floatNote("Kleurenbom! ✨", (sp.c + 0.5) * d, (sp.r + 0.5) * d, true);
        }
      }
      for (const f of step.fired) beamFx(f.r, f.c, f.special);
      sparkFx(step.cleared);
      if (step.cleared.length > 0) {
        floatNote(`+${step.points}`, sx / step.cleared.length, sy / step.cleared.length);
      }
      if (chain >= 3) {
        floatNote(`Combo ×${chain}! 🔥`, (W * d) / 2, (H * d) / 2 - d, true);
        shake();
      }
      sfx.clear();
      haptic(chain === 1 ? 10 : [10, 22, 10]);
      updateStatus();
      await sleep(300);
      if (s !== session) return;

      // Gravity + refill; rebuild the els grid from the engine's move report.
      const { moves, spawns } = applyGravity(board, rng, level.colors);
      const next: (HTMLElement | null)[][] = Array.from({ length: H }, () =>
        new Array<HTMLElement | null>(W).fill(null),
      );
      for (const m of moves) next[m.toR][m.c] = els[m.fromR][m.c];
      const perCol = new Array<number>(W).fill(0);
      for (const sp of spawns) perCol[sp.c]++;
      for (const sp of spawns) {
        const node = candyNode(sp.cell);
        // New candies start stacked just above the field and drop in.
        sizeAndPlace(node, sp.toR - perCol[sp.c], sp.c);
        layer.append(node);
        next[sp.toR][sp.c] = node;
      }
      els = next;
      void fieldEl.offsetWidth; // style flush so the drop transition animates
      for (const m of moves) {
        if (m.fromR === m.toR) continue;
        const node = els[m.toR][m.c];
        if (node) placeAt(node, m.toR, m.c);
      }
      for (const sp of spawns) {
        const node = els[sp.toR][sp.c];
        if (node) placeAt(node, sp.toR, sp.c);
      }
      await sleep(260);
      if (s !== session) return;
      const landed: Array<HTMLElement | null> = [];
      for (const m of moves) if (m.fromR !== m.toR) landed.push(els[m.toR][m.c]);
      for (const sp of spawns) landed.push(els[sp.toR][sp.c]);
      landFx(landed);
      chain++;
    }
  }

  function afterSettle(): void {
    busy = false;
    setCombo(1);
    updateStatus();
    if (score >= level.target) {
      win();
      return;
    }
    if (movesLeft <= 0) {
      showLose();
      return;
    }
    if (!findMove(board)) {
      floatNote("Geen zetten – schudden! 🔀", (W * d) / 2, (H * d) / 2, true);
      shuffleBoard(board, rng, level.colors);
      buildBoard();
      sfx.slide();
    }
  }

  // ---- Hint ----
  function showHint(): void {
    if (busy || finished || overlay) return;
    const move = findMove(board);
    if (!move) return;
    for (const [r, c] of move) {
      const node = els[r][c];
      if (!node) continue;
      node.classList.remove("sw-hint");
      void node.offsetWidth;
      node.classList.add("sw-hint");
      later(() => node.classList.remove("sw-hint"), 1500);
    }
    sfx.tap();
  }

  // ---- Win / lose ----
  function win(): void {
    if (finished) return;
    finished = true;
    sfx.win();
    haptic([20, 40, 20]);
    later(
      () =>
        opts.onWin({
          stars: starsFor(movesLeft, level.moves),
          score,
          moves: level.moves - movesLeft,
        }),
      420,
    );
  }

  function showLose(): void {
    if (finished || overlay) return;
    sfx.lose();
    haptic([10, 30, 10]);
    overlay = el(
      "div.sw-overlay",
      {},
      el(
        "div.sw-overlay-card",
        {},
        el("h3", {}, "Zetten op! 🍭"),
        el("p", {}, `Je haalde ${score} van de ${level.target} punten. Probeer het nog eens!`),
        el("button.sw-btn.primary", { onclick: doRestart }, "↺ Opnieuw"),
      ),
    );
    root.append(overlay);
  }

  function hideOverlay(): void {
    overlay?.remove();
    overlay = null;
  }

  // ---- Restart ----
  function doRestart(): void {
    session++;
    hideOverlay();
    rng = makeRng(level.seed);
    board = fillBoard(rng, level.colors);
    score = 0;
    movesLeft = level.moves;
    finished = false;
    busy = false;
    selected = null;
    pressCell = null;
    setCombo(1);
    buildBoard();
    updateStatus();
  }

  // ---- Init ----
  buildBoard();
  updateStatus();
  layout();

  return {
    destroy() {
      session++;
      for (const t of timers) clearTimeout(t);
      window.removeEventListener("resize", onResize);
    },
    restart: doRestart,
  };
}

export const sweetSwap: GameDef = {
  id: "sweetswap",
  title: "Sweet Swap",
  tagline: "Wissel snoepjes en match er drie",
  accent: "#ff6fae",
  icon: "🍬",
  levelCount: Infinity,
  createLevelView,
};
